import { AfterViewInit, ChangeDetectionStrategy, ChangeDetectorRef, Component, ElementRef, Input, OnDestroy, Signal, ViewChild, computed, forwardRef, signal } from '@angular/core';
import { ReactiveFormsModule, NG_VALUE_ACCESSOR, FormControl } from '@angular/forms';
import { CommonModule } from '@angular/common';
import { catchError, of, take, takeUntil, tap, EMPTY } from 'rxjs';
import { GridLayoutAnimationDirective, ReorderEvent, ReorderDirective, ReOrderableItemDirective } from '@newroom-connect/library/directives';
import { IActionButton, MediaItem, IFileHydrated, ICardItem, IFileItem, MediaItemDto, IFileItemDto, ICardItemDto } from '@newroom-connect/library/interfaces';
import { ButtonType, ActionRole, MediaType, Level } from '@newroom-connect/library/enums';
import { collapseAnimation, fadeInOutAnimation } from '@newroom-connect/library/animations';
import { TranslatePipe } from '@newroom-connect/library/pipes';
import { FileProcessingStatus } from '@newroom-connect/library/types';
import { FileProcessingService, FileService } from '@newroom-connect/library/services';

import { ButtonComponent } from '../buttons';
import { CheckboxComponent } from '../checkbox/checkbox.component';
import { InputCheckboxComponent, InputFileComponent } from '../inputs';
import { InputComponent } from '../inputs/input.component';
import { AlertComponent } from '../alert/alert.component';

import { MediaLibraryService } from './services/media-library.service';
import { MediaLibraryCardItemComponent } from './media-library-card-item/media-library-card-item.component';
import { MediaLibraryEmptyComponent } from './media-library-empty/media-library-empty.component';
import { MediaLibraryHeaderComponent } from './media-library-header/media-library-header.component';
import { MediaLibraryItemPropertiesComponent } from './media-library-item-properties/media-library-item-properties.component';

interface IMediaLibraryOutput {
  data: MediaItemDto[];
  openInFullscreen: boolean;
  gridLayoutStretched: boolean;
  disableDownload: boolean;
}

@Component({
  selector: 'nrc-media-library',
  templateUrl: './media-library.component.html',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    ButtonComponent,
    CheckboxComponent,
    InputCheckboxComponent,
    InputFileComponent,
    MediaLibraryEmptyComponent,
    MediaLibraryHeaderComponent,
    MediaLibraryCardItemComponent,
    MediaLibraryItemPropertiesComponent,
    GridLayoutAnimationDirective,
    AlertComponent,
    TranslatePipe,
    ReorderDirective,
    ReOrderableItemDirective
  ],
  animations: [collapseAnimation, fadeInOutAnimation],
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => MediaLibraryComponent),
      multi: true
    }
  ]
})
export class MediaLibraryComponent extends InputComponent<IMediaLibraryOutput> implements AfterViewInit, OnDestroy {
  @Input({ required: true }) public apiBaseUrl!: string;
  @Input({ required: true }) public projectId!: string;

  @Input() public title?: string;
  @Input() public maxCardLevel?: number;
  @Input() public mediaTypes?: string[] = [];
  @Input() public supportLayoutViewSettings = true;

  @ViewChild('gridContainer') public gridContainerElement?: ElementRef<HTMLDivElement>;

  public readonly isFileLibraryVisible = signal<boolean>(false);
  public readonly showExportToPdfAlert = signal<boolean>(false);
  public readonly selectedItemForExportPdf = signal<IFileHydrated | null>(null);

  public readonly fileLibraryFormControl = new FormControl<string | null>(null);
  public readonly gridLayoutStretched = new FormControl<boolean>(false, { nonNullable: true });

  public readonly buttonType = ButtonType;
  public readonly actionRole = ActionRole;
  public readonly mediaType = MediaType;
  public readonly level = Level;

  public readonly gridColumnsClass = computed<string>(() => {
    // Explicitly track dependencies for reactivity
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const cardChangeSignal = this.mediaLibraryService.cardChangeSignal();
    const itemCount = this.mediaLibraryService.openedCard().controls.length;
    const isStretched = this.mediaLibraryService.gridLayoutStretchedSignal();
    const isPropertiesCollapsed = this.mediaLibraryService.isPropertiesCollapsed();

    if (isStretched && this.mediaLibraryService.navigationHistory().length === 1) {
      return 'grid-cols-3';
    }

    if (itemCount === 0) {
      return 'grid-cols-1';
    }

    if (itemCount <= 2) {
      return 'grid-cols-2';
    }

    if (isPropertiesCollapsed && itemCount > 4) {
      return 'grid-cols-4';
    }

    return 'grid-cols-3';
  });

  /**
   * Computed signal for checking if item selection is active.
   */
  public readonly isItemSelectionActive = computed(() => this.mediaLibraryService.isSelectionModeActive());

  /**
   * Computed signal for header action buttons.
   */
  public readonly headerActionButtons: Signal<IActionButton[]> = computed(() => {
    // Force reactivity by explicitly tracking opened card changes
    const openedCard = this.mediaLibraryService.openedCard();
    const isEverySelected = this.mediaLibraryService.isEveryPageItemSelected();
    const isSomeButNotEverySelected = this.mediaLibraryService.isSomeButNotEveryPageItemSelected();

    // Check if any items are selected in the currently opened card
    const hasSelectedItems = openedCard.controls.some(control =>
      control.get('isSelected')?.value === true
    );

    return [
      {
        id: 'delete',
        icon: 'trash',
        buttonType: ButtonType.ICON,
        role: ActionRole.ERROR,
        // Enable delete button if any items are selected
        disabled: !hasSelectedItems &&
                  !isEverySelected &&
                  !isSomeButNotEverySelected
      },
      {
        id: 'createCard',
        icon: 'folder',
        buttonType: ButtonType.ICON,
        role: ActionRole.TRANSPARENT_SECONDARY,
        disabled: !this.canCreateCard()
      },
      {
        id: 'addFile',
        icon: 'upload',
        buttonType: ButtonType.ICON,
        role: ActionRole.TRANSPARENT_SECONDARY
      }
    ];
  });

  public readonly canCreateCard = computed(() =>
    this.maxCardLevel !== undefined && this.mediaLibraryService.navigationHistory().length - 1 < this.maxCardLevel
  );

  /**
   * @constructor
   *
   * @param mediaLibraryService
   * @param fileService
   * @param fileProcessingService
   * @param cdRef
   */
  constructor(
    public readonly mediaLibraryService: MediaLibraryService,
    private readonly fileService: FileService,
    private readonly fileProcessingService: FileProcessingService,
    private readonly cdRef: ChangeDetectorRef
  ) {
    super();
  }

  /**
   * Initialize component after view initialization.
   */
  public override ngAfterViewInit(): void {
    if (this.formControl) {
      this.mediaLibraryService.initialize(this.formControl.value ?? {
        data: [],
        openInFullscreen: false,
        gridLayoutStretched: false,
        disableDownload: false
      });

      // Subscribe to form changes from the service
      this.mediaLibraryService.mediaLibraryForm$
        .pipe(takeUntil(this.destroy$))
        .subscribe(form => {
          if (!form || !form.value) {
            return;
          }

          const processedItemsValue = this.processMediaLibraryActionValue(form.value as MediaItem[]);

          const newValue = {
            data: processedItemsValue,
            openInFullscreen: this.mediaLibraryService.openInFullscreen?.value ?? false,
            gridLayoutStretched: this.mediaLibraryService.gridLayoutStretched?.value ?? false,
            disableDownload: this.mediaLibraryService.disableDownload?.value ?? false
          };

          this.formControl.patchValue(newValue, { emitEvent: true });
          this.writeValue(newValue);
        });

      if (!this.supportLayoutViewSettings || !this.mediaLibraryService.openInFullscreen || !this.mediaLibraryService.gridLayoutStretched || !this.mediaLibraryService.disableDownload) {
        return;
      }

      const updateFormValues = (updates: Partial<{openInFullscreen: boolean, gridLayoutStretched: boolean, disableDownload: boolean}>): void => {
        const newValue = {
          ...this.formControl.value,
          openInFullscreen: this.mediaLibraryService.openInFullscreen?.value ?? false,
          gridLayoutStretched: this.mediaLibraryService.gridLayoutStretched?.value ?? false,
          disableDownload: this.mediaLibraryService.disableDownload?.value ?? false,
          ...updates
        };

        this.formControl.patchValue(newValue, { emitEvent: true });
        this.writeValue(newValue);
      };

      this.mediaLibraryService.gridLayoutStretched.valueChanges
        .pipe(takeUntil(this.destroy$))
        .subscribe(value => updateFormValues({ gridLayoutStretched: value }));

      this.mediaLibraryService.openInFullscreen.valueChanges
        .pipe(takeUntil(this.destroy$))
        .subscribe(value => updateFormValues({ openInFullscreen: value }));

      this.mediaLibraryService.disableDownload.valueChanges
        .pipe(takeUntil(this.destroy$))
        .subscribe((value: boolean) => updateFormValues({ disableDownload: value }));
    }
  }

  /**
   * Clean up on component destroy.
   */
  public override ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();

    this.mediaLibraryService.clearSelection();
    this.mediaLibraryService.isPropertiesCollapsed.set(false);
    super.ngOnDestroy();
  }

  /**
   * Handles header action button events.
   * @param actionButton Button that triggered the event.
   */
  public handleHeaderActionButtonEvent(actionButton: IActionButton): void {
    switch (actionButton.id) {
      case 'createCard': {
        this.mediaLibraryService.addItem(MediaType.CARD);
        break;
      }
      case 'delete': {
        const currentCard = this.mediaLibraryService.openedCard();
        const selectedIndices = currentCard.controls
          .map((control, index) => control.get('isSelected')?.value ? index : -1)
          .filter(index => index !== -1);

        this.mediaLibraryService.deleteItems(selectedIndices);
        break;
      }
      case 'addFile': {
        this.isFileLibraryVisible.update(isVisible => !isVisible);
        break;
      }
      default:
        break;
    }
  }

  /**
   * Handles click event on card items.
   * If item selection is not active, opens the properties bar for the clicked item.
   * If item selection is active, toggles selection for the clicked item.
   *
   * @param index
   */
  public handleCardItemClickEvent(index: number): void {
    this.mediaLibraryService.handleItemSelection(index);
  }

  /**
   * Toggles selection for all items.
   */
  public toggleAllItemsIsSelected(): void {
    const isEverySelected = this.mediaLibraryService.isEveryPageItemSelected();

    this.mediaLibraryService.updateAllItemsSelection(!isEverySelected);
  }

  /**
   * Toggles item selection mode.
   */
  public toggleItemSelection(): void {
    this.mediaLibraryService.toggleSelectionMode();
  }

  /**
   * Handles double click event on card items.
   * @param index Index of the clicked card.
   */
  public handleOpenCardEvent(index: number): void {
    const openedCard = this.mediaLibraryService.openedCard();
    const cardItem = openedCard.at(index);

    if (!cardItem) {
      return;
    }

    this.mediaLibraryService.clearSelection();
    this.mediaLibraryService.navigateToFolder(cardItem);
  }

  /**
   * Handles changes in item properties.
   *
   * @param index
   * @param value
   */
  public handlePropertiesChangeEvent(index: number, value: Partial<MediaItem>): void {
    this.mediaLibraryService.updateItem(index, value);
  }

  /**
   * Handles empty card action event.
   */
  public handleEmptyCardActionEvent(): void {
    if (!this.canCreateCard()) {
      this.isFileLibraryVisible.update(isVisible => !isVisible);

      return;
    }

    this.mediaLibraryService.addItem(MediaType.CARD);
  }

  /**
   * Handles file library modal close event.
   * @param file Selected file from modal.
   */
  public handleFileLibraryModalCloseEvent(file: IFileHydrated): void {
    if (!this.fileLibraryFormControl) {
      return;
    }

    if (!FileService.SUPPORTED_TRANSLATION_MIMETYPES.includes(file.mimetype)) {
      this.mediaLibraryService.addItem(this.mediaType.FILE, {
        type: MediaType.FILE,
        file,
        fileId: file.id,
        thumbnailId: null,
        fileTags: file.tags,
        label: file.title,
        isSelected: false
      });

      this.fileLibraryFormControl.reset();
      this.isFileLibraryVisible.update(isVisible => !isVisible);

      return;
    }

    // After an item has been added, check if it's a supported document mimetype
    this.showExportToPdfAlert.set(true);
    this.selectedItemForExportPdf.set(file);
  }

  /**
   * Handles reorder events from the drag-and-drop directive.
   * Calls the service to reorder items and updates the form control value for saving.
   *
   * @param event The reorder event containing fromIndex and toIndex.
   */
  public handleReorderEvent(event: ReorderEvent): void {
    const { fromIndex, toIndex } = event;

    this.mediaLibraryService.reorderItems(fromIndex, toIndex, this.mediaLibraryService.navigationHistory().length - 1);
  }

  /**
   *
   */
  public handleFileExportPdfAlertActionEvent(): void {
    this.showExportToPdfAlert.set(false);

    const file = this.selectedItemForExportPdf();

    if (!file) {
      return;
    }

    this.mediaLibraryService.addItem(this.mediaType.FILE, {
      type: MediaType.FILE,
      file,
      fileId: file.id,
      thumbnailId: null,
      fileTags: file.tags,
      label: file.title,
      isSelected: false
    });

    this.selectedItemForExportPdf.set(null);
  }

  /**
   *
   */
  public handleFileExportPdfEvent(): void {
    const file = this.selectedItemForExportPdf();

    if (!file) {
      return;
    }

    this.fileService.exportFileAsPdf(this.projectId, file.id).pipe(
      take(1)
    ).subscribe((result) => {
      if (!result.sessionId || !result.success || !file) {
        return;
      }

      this.fileService.getFile(this.projectId, file.id).pipe(
        take(1),
        catchError(() => {
          this.showExportToPdfAlert.set(false);
          this.selectedItemForExportPdf.set(null);

          return of(null);
        })
      ).subscribe((updatedFile) => {
        this.showExportToPdfAlert.set(false);
        this.selectedItemForExportPdf.set(null);

        if (!updatedFile) {
          return;
        }

        this.mediaLibraryService.addItem(MediaType.FILE, {
          type: MediaType.FILE,
          file: updatedFile,
          fileId: updatedFile.id,
          thumbnailId: null,
          fileTags: updatedFile.tags,
          label: updatedFile.title,
          isSelected: false
        });

        this.fileProcessingService.watchProgress(this.projectId, 'export-pdf', result.sessionId, updatedFile.id)
          .pipe(
            tap(progress => {
              // Handle completion
              if (progress.status === FileProcessingStatus.COMPLETED) {
                const index = this.mediaLibraryService.openedCard().controls.findIndex(control => control.get('file')?.value?.id === file.id);

                if (index === -1) {
                  return;
                }

                this.mediaLibraryService.updateItem(index, {
                  type: MediaType.FILE,
                  file: updatedFile,
                  fileId: updatedFile.id,
                  thumbnailId: null,
                  fileTags: updatedFile.tags,
                  label: updatedFile.title,
                  isSelected: false
                });

                this.cdRef.detectChanges();
              }
            }),
            catchError(() => {
              this.showExportToPdfAlert.set(false);
              this.selectedItemForExportPdf.set(null);
              return EMPTY;
            })
          )
          .subscribe();
      });
    });
  }

  /**
   * Processes media library internal format to the API's MediaItemDto format for submission.
   *
   * @param value Array of media items to process.
   *
   * @returns Processed media items with only ID references for form control.
   */
  private processMediaLibraryActionValue(value: MediaItem[]): MediaItemDto[] {
    const processItem = (item: MediaItem): MediaItemDto => {
      const { type, label, thumbnailId } = item;
      const base = { type, label, thumbnailId };

      if (item.type === MediaType.FILE) {
        return {
          ...base,
          fileId: (item as IFileItem).fileId,
          fileTags: (item as IFileItem).fileTags || []
        } as IFileItemDto;
      }

      return {
        ...base,
        isThumbnailAsDetailImage: (item as ICardItem).isThumbnailAsDetailImage,
        items: (item as ICardItem).items?.map(processItem) ?? null,
        description: (item as ICardItem).description
      } as ICardItemDto;
    };

    return value.map(processItem);
  }
}
